import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { fetchAuthSession } from "aws-amplify/auth";

// MT-012 — Contexto de tenant seleccionado por el SUPER_ADMIN. Persiste en sessionStorage
// y se envía como header X-Tenant-Context en cada request. El valor ALL = scope global.
export const TENANT_CONTEXT_STORAGE_KEY = "grups.bo.tenantContext";
export const TENANT_CONTEXT_ALL = "ALL";
export const TENANT_CONTEXT_HEADER = "X-Tenant-Context";
export const TENANT_CONTEXT_CHANGED_EVENT = "grups.bo.tenantContextChanged";

/**
 * Header de override de tenant para un request puntual. Se usa cuando el
 * SUPER_ADMIN, estando en scope global ("Todos"), elige el tenant destino desde
 * la sección o el formulario. Devuelve {} si no hay tenant elegido, para no pisar
 * el del selector superior. El backend ignora el header si el usuario no es
 * SUPER_ADMIN, así que no habilita nada que el rol no permita.
 */
export const tenantContextHeader = (
  tenantId?: string | null
): Record<string, string> =>
  tenantId ? { [TENANT_CONTEXT_HEADER]: tenantId } : {};

export const readBoTenantContext = (): string =>
  sessionStorage.getItem(TENANT_CONTEXT_STORAGE_KEY) || TENANT_CONTEXT_ALL;

/** Persiste el mismo valor que usa el selector del topbar (X-Tenant-Context). */
export const setBoTenantContext = (tenantId: string | null): void => {
  sessionStorage.setItem(
    TENANT_CONTEXT_STORAGE_KEY,
    tenantId && tenantId !== TENANT_CONTEXT_ALL ? tenantId : TENANT_CONTEXT_ALL
  );
  window.dispatchEvent(new Event(TENANT_CONTEXT_CHANGED_EVENT));
};

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API,
  prepareHeaders: async (headers) => {
    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString() ?? "";
    const idToken = session.tokens?.idToken?.toString() ?? "";
    // Set default content type for requests with body
    if (headers.get("Content-Type") === "multipart/form-data") {
      // El navegador debe setear el boundary del multipart: no forzar Content-Type.
      headers.delete("Content-Type");
    } else if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }

    if (!accessToken || !idToken) {
      return headers;
    }

    if (!!accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    // MT-012 — Adjuntar el tenant seleccionado (salvo scope global "ALL").
    // Si el endpoint ya fijó el header (override por request: el SUPER_ADMIN eligió
    // un tenant destino en la sección/formulario), ese valor tiene prioridad.
    if (!headers.has(TENANT_CONTEXT_HEADER)) {
      const selectedTenant = sessionStorage.getItem(TENANT_CONTEXT_STORAGE_KEY);
      if (selectedTenant && selectedTenant !== TENANT_CONTEXT_ALL) {
        headers.set(TENANT_CONTEXT_HEADER, selectedTenant);
      }
    }

    return headers;
  },
});

export const api = createApi({
  reducerPath: "ebAPI",
  baseQuery: baseQuery,
  refetchOnMountOrArgChange: true,
  refetchOnFocus: true,
  refetchOnReconnect: true,

  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: [
    "notificationLogs",
    "events",
    "users",
    "tenants",
    "tenantDomains",
    "boUsers",
    "notifications",
    "ratings",
    "reports",
    "messaging",
    "venues",
    "workshops",
    "institutionalCommunications",
    "rulesFictional",
    "rulesFeaturedBadge",
    "rulesEventAdvancedConfig",
    "featureFlags",
    "benefitsMetrics",
    "benefitSettings",
    "benefitFeaturedEvents",
    "benefitFeatured",
    "benefitCategories",
    "benefitPartners",
    "benefitLocations",
    "benefits",
    "supportCategories",
    "supportArticles",
    "supportReasons",
    "supportConfig",
    "supportContacts",
    "supportFeedback",
    "cancellationReasons",
    "eventCancellations",
    "activityMetrics",
    "shareSettings",
    "eventTypes",
    "eventSubTypes",
    "homeSections",
    "banners",
    "attendances",
    "attendanceConfig",
    "eventAttendanceRoster",
    "boNotifications",
    "pointRules",
    "pointMetrics",
    "pointRewards",
    "pointRedemptions",
    "pointLedger",
    "pointSettings",
  ],
  endpoints: (build) => ({
    // Authentication endpoints
    login: build.mutation<any, { email: string; password: string }>({
      query: (credentials) => ({
        url: "/auth/login",
        method: "POST",
        body: credentials,
      }),
    }),
    refreshToken: build.mutation<any, { refreshToken: string }>({
      query: (data) => ({
        url: "/auth/refresh",
        method: "POST",
        body: data,
      }),
    }),
    getCurrentUser: build.query<any, void>({
      query: () => "/auth/me",
    }),
    logout: build.mutation<any, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
    }),
    
    // System endpoints
    healthCheck: build.query<any, void>({
      query: () => "/health/",
    }),
    getSystemVersion: build.query<any, void>({
      query: () => "/system/version",
    }),

    // MT-012 — Identidad + scope del usuario de BO y tenants disponibles para el selector.
    getBoMe: build.query<BoMeResponse, void>({
      query: () => "/bo/me",
    }),
  }),
});

export interface BoMeTenant {
  id: string;
  provider: string;
  theme: string;
}

export interface BoMeResponse {
  userId: number;
  role: "SUPER_ADMIN" | "TENANT_ADMIN" | "MEMBER";
  tenantId: string;
  scope: { type: "global" } | { type: "tenant"; tenantId: string };
  defaultTenantId: string;
  tenants: BoMeTenant[];
}

export const { useGetBoMeQuery } = api;
